import { Component, NgZone, OnInit, OnDestroy } from '@angular/core';
import { IonApp, IonRouterOutlet, NavController, Platform } from '@ionic/angular/standalone';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ToastService } from './services/toast.service';
import { MessagingService } from './services/messaging.service';
import { ConfirmService } from './services/confirm.service';
import { ProfileDataService } from './services/profile-data.service';
import { PrimeNG } from 'primeng/config';
import { register } from 'swiper/element/bundle';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Router } from '@angular/router';
import { PushNotifications } from '@capacitor/push-notifications';
import { Auth } from '@angular/fire/auth';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PluginListenerHandle } from '@capacitor/core';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet, ToastModule, ConfirmDialogModule],
  providers: [
    MessageService,
    MessagingService,
    ToastService,
    ConfirmationService,
    ConfirmService
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private hasNavigated = false;
  private resumeListener?: PluginListenerHandle;

  constructor(
    private _config: PrimeNG,
    private _router: Router,
    private _zone: NgZone,
    private _nav: NavController,
    private _platform: Platform,
    private _profileDataService: ProfileDataService,
    private _auth: Auth
  ) {
    this.initializeApp();
    this.configurePrimeNG();
 
  }

  private initializeApp(): void {
    this._platform.ready().then(async () => {
      // Initialize StatusBar to prevent layout jumping
      if (this._platform.is('capacitor')) {
        try {
          await StatusBar.setOverlaysWebView({ overlay: false });
          await StatusBar.setStyle({ style: Style.Light });
          await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
        } catch (error) {
          console.error('Error setting StatusBar:', error);
        }

        // Fix iOS viewport shift when returning from native apps (scanner, camera, etc.)
        this.resumeListener = await App.addListener('resume', () => {
          this.fixViewportPosition();
        });
      }

      App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
        await new Promise(resolve => setTimeout(resolve, 500));
        this._zone.run(() => {
          const slug = event.url.split('.com').pop();
          console.log('Deep link received:', slug);

          if (slug) {
            this.hasNavigated = true;
            this._nav.navigateRoot(slug);
          }
        });
      });

      // Initiales Routing basierend auf Platform
      this.handleInitialRouting();
    });
  }

  private async handleInitialRouting(): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 500));

    const isCapacitor = this._platform.is('capacitor');
    const currentUrl = this._router.url;

    // Nur auf Root-Route reagieren
    if (currentUrl !== '/' && currentUrl !== '') {
      return;
    }
    if (isCapacitor) {

      // Native App: Auth-basiertes Routing
      this._auth.onAuthStateChanged((user) => {
        if (user) {
          if (!this.hasNavigated) {
            this.hasNavigated = true;
            this._nav.navigateRoot('/dashboard/hub');
          }
        } else {
          // User nicht eingeloggt -> Login
          if (!this.hasNavigated) {
            this.hasNavigated = true;
            this._nav.navigateRoot('/auth/landing');
          }
        }
      });
    } else {
      // Web Browser
      this._auth.onAuthStateChanged((user) => {
        if (!this.hasNavigated) {
          if (user) {
            // Eingeloggt -> Dashboard
            this.hasNavigated = true;
            this._nav.navigateRoot('/dashboard/hub');
          } else {
            // Nicht eingeloggt -> Landing Page (zeigt mobilen Usern einen Dialog)
            this.hasNavigated = true;
            this._nav.navigateRoot('/landing');
          }
        }
      });
    }
  }

  async ngOnInit(): Promise<void> {
    try {
      await PushNotifications.removeAllDeliveredNotifications();
    } catch (e) {
      console.error('Error removing notifications:', e);
    }

    // Warte auf Deep-Link
    await new Promise(resolve => setTimeout(resolve, 1200));

    // Falls kein Deep-Link und auf Capacitor, prüfe ob wir noch auf Root sind
    if (!this.hasNavigated && this._platform.is('capacitor')) {
      const currentUrl = this._router.url;
      if (currentUrl === '/' || currentUrl === '') {
        // Auth State wird im handleInitialRouting behandelt
      }
    }
  }

  private configurePrimeNG(): void {
    this._config.setTranslation({
      startsWith: 'Beginnt mit',
      contains: 'Enthält',
      notContains: 'Enthält nicht',
      endsWith: 'Endet mit',
      equals: 'Gleich',
      notEquals: 'Ungleich',
      noFilter: 'Kein Filter',
      lt: 'Kleiner als',
      today: 'Heute',
      clear: 'Löschen',
      matchAll: 'Alle übereinstimmen',
      matchAny: 'Beliebige übereinstimmen',
      addRule: 'Regel hinzufügen',
      removeRule: 'Regel entfernen',
      emptyFilterMessage: 'Keine Ergebnisse gefunden',
      emptySearchMessage: 'Keine Ergebnisse gefunden',
      emptyMessage: 'Keine Einträge gefunden',
      accept: 'Akzeptieren',
      reject: 'Ablehnen',
      choose: 'Wählen',
      cancel: 'Abbrechen',
      upload: 'Hochladen',
      apply: 'Anwenden',
      monthNames: [
        'Januar',
        'Februar',
        'März',
        'April',
        'Mai',
        'Juni',
        'Juli',
        'August',
        'September',
        'Oktober',
        'November',
        'Dezember',
      ],
      dayNames: [
        'Sonntag',
        'Montag',
        'Dienstag',
        'Mittwoch',
        'Donnerstag',
        'Freitag',
        'Samstag',
      ],
      dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    });
  }

  /**
   * Fix iOS viewport shift issue when returning from native apps (scanner, camera, etc.)
   * This is a known iOS WebView bug where the viewport gets pushed up and stays there.
   */
  private async fixViewportPosition(): Promise<void> {
    // Re-initialize StatusBar to force correct safe area calculation
    try {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
    } catch (error) {
      console.error('Error resetting StatusBar:', error);
    }

    setTimeout(() => {
      // Reset scroll positions
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;

      // Force a reflow to fix the viewport
      document.body.style.height = '100%';
      document.documentElement.style.height = '100%';

      // Reset any transforms that might have been applied
      const ionApp = document.querySelector('ion-app');
      if (ionApp) {
        (ionApp as HTMLElement).style.transform = 'none';
        (ionApp as HTMLElement).style.position = 'fixed';
        (ionApp as HTMLElement).style.top = '0';
        (ionApp as HTMLElement).style.left = '0';
        (ionApp as HTMLElement).style.right = '0';
        (ionApp as HTMLElement).style.bottom = '0';
      }

      // Trigger a resize event to force recalculation
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  ngOnDestroy(): void {
    this.resumeListener?.remove();
  }
}